/* eslint-disable no-param-reassign */
import Phaser from 'phaser';

const collisionCanvas = document.createElement('canvas');
const collisionCtx = collisionCanvas.getContext('2d');
collisionCtx.save();

const collisionCanvas2 = document.createElement('canvas');
const collisionCtx2 = collisionCanvas2.getContext('2d');
collisionCtx2.save();

/** @type {Phaser.GameObjects.Image[]} */
const collisionImages = [];

/**
 * Returns two images containing the collision canvases. Add these to your scene.
 * @param {Phaser.Scene} scene
 */
export function getCollisionImages(scene) {
  // check to see if the scene contains the collision canvas key
  if (!scene.textures.exists('collisioncanvas0')) {
    scene.textures.addCanvas('collisioncanvas0', collisionCanvas);
  }
  if (!scene.textures.exists('collisioncanvas1')) {
    scene.textures.addCanvas('collisioncanvas1', collisionCanvas2);
  }
  const images = [new Phaser.GameObjects.Image(scene, 0, 0, 'collisioncanvas0').setOrigin(0), new Phaser.GameObjects.Image(scene, 0, 0, 'collisioncanvas1').setOrigin(0)];
  images.forEach((i) => {
    collisionImages.push(i);
    i.once(Phaser.GameObjects.Events.DESTROY, () => Phaser.Utils.Array.Remove(collisionImages, i));
  });
  return images;
}

/**
 * Updates the canvas textures so that debug images are updated.
 * @param {Phaser.Scene} scene
 */
export function updateCanvasTextures(scene) {
  if (scene.textures.exists('collisioncanvas0')) {
    scene.textures.get('collisioncanvas0').update();
  }
  if (scene.textures.exists('collisioncanvas1')) {
    scene.textures.get('collisioncanvas1').update();
  }
  collisionImages.forEach((i) => {
    if (i.texture.key === 'collisioncanvas0') i.setDisplaySize(collisionCanvas.width, collisionCanvas.height);
    if (i.texture.key === 'collisioncanvas1') i.setDisplaySize(collisionCanvas2.width, collisionCanvas2.height);
  });
}

/**
 * Determines if two images are colliding. Checks at a pixel level, not just the bounding box.
 * @param {Phaser.GameObjects.Image | Phaser.GameObjects.Sprite} bitmap1 
 * @param {Phaser.GameObjects.Image | Phaser.GameObjects.Sprite} bitmap2
 * @param {number} alphaThreshold from 0 to 0.99999; default is 0
 * @returns 
 */
export function checkPixelCollision(bitmap1, bitmap2, alphaThreshold = 0) {
  const intersection = Phaser.Geom.Rectangle.Intersection(bitmap1.getBounds(), bitmap2.getBounds());
  if (intersection.width < 1 || intersection.height < 1) {
    return false;
  }

  alphaThreshold = alphaThreshold || 0;
  alphaThreshold = Phaser.Math.Clamp(alphaThreshold, 0, 0.99999);

  // setting the canvas size
  collisionCanvas.width = intersection.width;
  collisionCanvas.height = intersection.height;
  collisionCanvas2.width = intersection.width;
  collisionCanvas2.height = intersection.height;

  const imageData1 = _intersectingImagePart(intersection, bitmap1, collisionCtx);
  const imageData2 = _intersectingImagePart(intersection, bitmap2, collisionCtx2);

  // compare the alpha values to the threshold and return the result
  // = true if pixels are both > alphaThreshold at one coordinate
  return _compareAlphaValues(imageData1, imageData2, intersection.width, intersection.height, alphaThreshold);
}

/**
 * 
 * @param {Phaser.Geom.Rectangle} intersetion
 * @param {Phaser.GameObjects.Image | Phaser.GameObjects.Sprite} bitmap
 * @param {CanvasRenderingContext2D} ctx
 * @returns 
 */
function _intersectingImagePart(intersetion, bitmap, ctx) {
  const image = bitmap.frame.source.image;

  const bl = bitmap.getLocalPoint(intersetion.x, intersetion.y);
  ctx.restore();
  ctx.save();
  ctx.rotate(_getParentalCumulatedProperty(bitmap, 'rotation'));
  ctx.scale(_getParentalCumulatedProperty(bitmap, 'scaleX', '*'), _getParentalCumulatedProperty(bitmap, 'scaleY', '*'));
  ctx.translate(-bl.x, -bl.y);

  /** @type {x:number, y:number, width:number, height:number} */
  const sr = bitmap.frame.canvasData;
  if (sr !== undefined) {
    ctx.drawImage(image, sr.x, sr.y, sr.width, sr.height, 0, 0, sr.width, sr.height);
  }
  else {
    ctx.drawImage(image, 0, 0, image.width, image.height);
  }
  return ctx.getImageData(0, 0, intersetion.width, intersetion.height).data;
}

/**
 * 
 * @param {Uint8ClampedArray} imageData1
 * @param {Uint8ClampedArray} imageData2
 * @param {number} width 
 * @param {number} height 
 * @param {number} alphaThreshold 
 * @returns 
 */
function _compareAlphaValues(imageData1, imageData2, width, height, alphaThreshold) {
  let offset = 3;

  // parsing through the pixels checking for an alpha match
  for (let y = 0; y < height; ++y) {
    for (let x = 0; x < width; ++x) {
      const alpha1 = imageData1.length > offset + 1 ? imageData1[offset] / 255 : 0;
      const alpha2 = imageData2.length > offset + 1 ? imageData2[offset] / 255 : 0;

      if (alpha1 > alphaThreshold && alpha2 > alphaThreshold) {
        return true;
      }
      offset += 4;
    }
  }

  return false;
}

/**
 * This is needed to paint the intersection part correctly, if the tested bitmap is a child to a rotated/scaled parent this was not painted correctly before
 * @param {Phaser.GameObjects.Image | Phaser.GameObjects.Sprite} child
 * @param {string} propName 
 * @param {string} operation needs to be '+' or '*'; defaults to '+'
 * @returns {number}
 */
function _getParentalCumulatedProperty(child, propName, operation) {
  operation = operation || '+';
  if (child.parentContainer && child.parentContainer[propName]) {
    const cp = child[propName];
    const pp = _getParentalCumulatedProperty(child.parentContainer, propName, operation);
    if (operation === '*') {
      return cp * pp;
    }
    else {
      return cp + pp;
    }
  }

  return child[propName];
}